import { NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DenseTable } from '../components/denseTable/DenseTable';
import { DenseTableColumn } from '../components/denseTable/DenseTable.types';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { StateChip } from '../components/stateChip/StateChip';
import { formatTimestamp } from '../overviewTab/dodFormat';

import * as Styled from './WorkerTab.styles';
import { WorkerEvent, ageMsSince, formatDurationMs, workerEventState } from './worker.helpers';

export type WorkerEventsListProps = {
  events: WorkerEvent[];
  now: number;
};

type WorkerEventRow = WorkerEvent & { id: string };

/**
 * Connection events, newest first, in the application's table: the moment, how long ago, the
 * event as the same state chip the session card uses (no raw wire names), the server and the
 * detail the worker attached.
 */
export const WorkerEventsList = ({ events, now }: WorkerEventsListProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const columns: Array<DenseTableColumn<WorkerEventRow>> = [
    {
      key: 'time',
      header: formatMessage({ id: AppMessages['dod.worker.events.column.time'] }),
      width: '150px',
      render: (row) => formatTimestamp(Date.parse(row.at)),
    },
    {
      key: 'ago',
      header: formatMessage({ id: AppMessages['dod.worker.events.column.ago'] }),
      width: '120px',
      render: (row) =>
        formatMessage(
          { id: AppMessages['dod.queues.relativeAgo'] },
          { time: formatDurationMs(ageMsSince(row.at, now)) },
        ),
    },
    {
      key: 'event',
      header: formatMessage({ id: AppMessages['dod.worker.events.column.event'] }),
      width: '150px',
      render: (row) => <StateChip state={workerEventState(row.event)} />,
    },
    {
      key: 'rcp',
      header: formatMessage({ id: AppMessages['dod.worker.events.column.rcp'] }),
      width: 'minmax(140px, 1fr)',
      render: (row) => row.rcpName || row.rcpId,
    },
    {
      key: 'detail',
      header: formatMessage({ id: AppMessages['dod.worker.events.column.detail'] }),
      width: 'minmax(160px, 1.5fr)',
      render: (row) => row.detail || '—',
    },
  ];

  const rows = events.map((event, index) => ({ ...event, id: `${event.at}-${event.rcpId}-${index}` }));

  return (
    <SectionCard
      title={formatMessage({ id: AppMessages['dod.worker.events'] })}
      actions={
        <Styled.ToolbarLabel>{formatMessage({ id: AppMessages['dod.worker.events.caption'] })}</Styled.ToolbarLabel>
      }
    >
      {events.length === 0 ? (
        <NothingToShow text={formatMessage({ id: AppMessages['dod.worker.events.empty'] })} />
      ) : (
        <div data-testid="dod-worker-events">
          <DenseTable<WorkerEventRow> columns={columns} data={rows} />
        </div>
      )}
    </SectionCard>
  );
};
